import { Component, Inject, OnDestroy } from "@angular/core";
import { MD_DIALOG_DATA, MdDialogRef } from "@angular/material";
import { TranslationService } from "../../services/translation.service";
import { Store } from "@ngrx/store";
import { User } from "../../models/user";
import { Subscription } from "rxjs/Subscription";
import { Translation } from "../../models/translation";
import { ProgramArticleTranslation } from "../../models/program-article-translation";
import { TranslationsActions } from "../../store/actions/translations.actions";
import { AdminService } from "../../services/admin.service";
import { ProgramArticlePictureETTValue } from "../../models/program-article-translation-value";

@Component({
  selector: "app-pictogram-modal",
  templateUrl: "./pictogram-modal.component.html",
  styleUrls: ["./pictogram-modal.component.scss"],
})
export class PictogramModalComponent implements OnDestroy {
  subscriptions: Subscription[];
  translation: Translation | ProgramArticleTranslation;
  pictogram: ProgramArticlePictureETTValue;
  newPicto: string;
  isPlaceholder: boolean;
  loading: boolean;
  saving: boolean;
  errorMessage: string;
  selectedIndex: number;
  editMode: boolean;
  isReseted: boolean;
  isDeleted: boolean;

  /**
   * Initialize variables and load data from program article or translation
   * @param {MdDialogRef<PictogramModalComponent>} dialogRef
   * @param data
   * @param {TranslationService} translationService
   * @param {TranslationsActions} translationsActions
   * @param {AdminService} adminService
   * @param {Store<any>} store
   */
  constructor(
    public dialogRef: MdDialogRef<PictogramModalComponent>,
    @Inject(MD_DIALOG_DATA) public data: any,
    private translationService: TranslationService,
    private translationsActions: TranslationsActions,
    private adminService: AdminService,
    private store: Store<any>
  ) {
    this.loading = true;
    this.isReseted = false;
    this.isDeleted = false;
    this.saving = false;
    this.subscriptions = [];
    this.subscriptions.push(
      this.store.select("userState").subscribe((user: User) => {
        if (user && user.currentLanguage) {
          if (!this.data.optional2) {
            this.selectedIndex = 0;
          } else {
            const indexes = ["Pictogram"];
            this.selectedIndex = indexes.indexOf(this.data.optional2);
          }

          if (data.type === "translate") {
            this.adminService.init().subscribe(() => {
              //Only full access and editors user can edit sport pictograms
              this.editMode =
                this.adminService.can("Translate", "Edit") &&
                (this.data.optional !== "S" ||
                  user.profiles.findIndex((p) => p.id == 1 || p.id == 16) > -1);
              this.getPictogramDataForTranslate();
            });
          }
        }
      })
    );
  }

  /**
   * Get the pictogram from the translation according to the current language
   */
  getPictogramDataForTranslate() {
    this.subscriptions.push(
      this.store.select("userState").subscribe((user: User) => {
        if (user && user.currentLanguage) {
          this.translationService
            .getTranslationById(
              this.data.id,
              this.data.optional,
              user.currentLanguage.id
            )
            .subscribe((translation) => {
              this.translation = translation;
              this.pictogram = this.translation.pictogram;
              this.isPlaceholder =
                this.pictogram.pictureURL.includes("placeholder");
              this.loading = false;
            });
        }
      })
    );
  }

  /**
   * When the picture is clicked, simulate a click on the upload input
   */
  upload() {
    document.getElementById("file_upload_picto").click();
  }

  /**
   * Handler the drop of picture
   */
  onDrop(event) {
    event.preventDefault();
    this.fileChange(event);
  }

  /**
   * Handler needed to activate the OnDrop Handler
   */
  onDragOver(event) {
    event.stopPropagation();
    event.preventDefault();
  }

  // noinspection JSMethodCanBeStatic
  /**
   * When a file is uploaded, load the data and check the size according to the minimal required size
   * @param event
   */
  fileChange(event) {
    if (
      (event.target.files && event.target.files[0]) ||
      (event.dataTransfer.files && event.dataTransfer.files[0])
    ) {
      this.loading = true;
      const file = event.target.files
        ? event.target.files[0]
        : event.dataTransfer.files[0];
      const reader = new FileReader();

      reader.addEventListener("load", () => {
        const image = new Image();
        image.src = reader.result as string;
        image.addEventListener("load", () => {
          if (image.width <= 800 && image.height <= 800) {
            this.errorMessage = null;
            this.pictogram.pictureURL = reader.result as string;
            this.newPicto = reader.result as string;
            this.loading = false;
          } else {
            this.errorMessage = "Image too big, it must be at most 800x800 px";
            setTimeout(() => (this.errorMessage = null), 5000);
            this.loading = false;
          }
        });
      });

      reader.readAsDataURL(file);
      this.isReseted = false;
      this.isDeleted = false;
    }
  }

  /**
   * Format the json and upload these data according to the picture type (program article or translation)
   */
  uploadPicture() {
    let translation = null;
    if (this.data.type === "translate") {
      translation = {
        id: this.translation.id,
        elementExternalId: this.translation["elementExternalId"],
        languageId: this.translation["languageId"],
        translationTypeId: this.translation["translationTypeId"],
        pictures: [],
      };
    }

    if (this.isReseted) {
      this.translationService
        .placeholderPictogram(translation)
        .subscribe((res) => {
          this.savingCallback(res);
          return;
        });
    } else if (this.isDeleted) {
      this.translationService
        .deletePictogram(translation, this.pictogram.pictureId)
        .subscribe((res) => {
          this.savingCallback(res);
          return;
        });
    }

    if (this.newPicto) {
      translation.pictures.push({
        pictureURL: null,
        blob: this.newPicto,
        pictureFormatCode: "Pictogram",
        pictureFormatName: "Pictogram",
      });
    }

    this.saving = true;
    this.dialogRef.disableClose = true;
    this.errorMessage = null;

    if (this.data.type === "translate") {
      this.translationService.updatePictures(translation).subscribe((res) => {
        this.savingCallback(res);
      });
    }
  }

  resetToPlaceholder() {
    this.isPlaceholder = true;
    this.newPicto = null;
    this.pictogram.pictureURL =
      "https://i.eurosport.com/taiga/Pictogram/placeholder1.png";
    this.isReseted = true;
    this.isDeleted = false;
  }

  deletePictogram() {
    this.isPlaceholder = false;
    this.newPicto = null;
    this.pictogram.pictureURL =
      "https://i.eurosport.com/taiga/Pictogram/transparent.png";
    this.isReseted = false;
    this.isDeleted = true;
  }

  /**
   * Check the upload response from the server
   * @param res
   */
  savingCallback(res) {
    this.saving = false;
    this.dialogRef.disableClose = false;
    if (res.success === 1) {
      if (this.data.type === "translate") {
        this.translationsActions.updateTranslationPicture(
          new Translation(res.value)
        );
      }
      this.dialogRef.close();
    } else {
      this.errorMessage = res.message;
      setTimeout(() => (this.errorMessage = null), 5000);
    }
  }

  /**
   * Unsubscribe all the subscriptions on destroy event
   */
  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/modals/pictogram-modal/pictogram-modal.component.ts